import { hashKey, type Query, type QueryClient } from '@tanstack/react-query'
import { queueKeys } from '../queue/queries'

export const LIVE_INVALIDATE_THROTTLE_MS = 500

const DETAIL_SEGMENT = 'detail'

export interface LiveInvalidatorOptions {
  queryClient: QueryClient
  tenantId: string
}

export interface LiveInvalidator {
  note: (conversationId: string) => void
  resync: () => void
  cancel: () => void
}

const isDetailQuery = (query: Query): boolean => query.queryKey[1] === DETAIL_SEGMENT

export const createLiveInvalidator = ({ queryClient, tenantId }: LiveInvalidatorOptions): LiveInvalidator => {
  let pending: ReadonlySet<string> = new Set()
  let timer: ReturnType<typeof setTimeout> | undefined

  const flush = () => {
    const changed = new Set([...pending].map((id) => hashKey(queueKeys.detail(tenantId, id))))
    pending = new Set()
    timer = undefined
    void queryClient.invalidateQueries({
      queryKey: queueKeys.all,
      predicate: (query) => !isDetailQuery(query) || changed.has(query.queryHash),
    })
  }

  return {
    note: (conversationId) => {
      pending = new Set([...pending, conversationId])
      if (timer === undefined) timer = setTimeout(flush, LIVE_INVALIDATE_THROTTLE_MS)
    },
    resync: () => void queryClient.invalidateQueries({ queryKey: queueKeys.all }),
    cancel: () => {
      clearTimeout(timer)
      timer = undefined
      pending = new Set()
    },
  }
}
